import { GNSS } from '@/utils/map';
import { ICloseFrame, IDetection, IDetectionsByFrame } from '@/types/features';
import { IPoints } from '@/types/map';
import { IOutcome } from '@/types/trainers';
import { ICoordinates } from '@/types/stats';

export const normalizeAzimuth = (angleDegrees: number) => {
  const normalizedAngle = angleDegrees % 360;
  return normalizedAngle >= 0 ? normalizedAngle : normalizedAngle + 360;
};

export const sortFramesByDate = (a: IFrame, b: IFrame) => {
  return new Date(a.t).getTime() - new Date(b.t).getTime();
};

export interface IFrame extends GNSS {
  url: string;
  idx: number;
  yaw?: number;
}

export const getImageArray = (
  frames: IFrame[] | undefined,
  closeFrames: IFrame[] | undefined,
) => {
  if (frames === undefined || closeFrames === undefined) return [];

  let imagesSource: IFrame[] = frames || [];
  if (!imagesSource?.length || imagesSource.length <= 2) {
    if (closeFrames && closeFrames.length > 0) {
      imagesSource = [...imagesSource, ...closeFrames];
    }
  }

  const urlSet = new Set();
  const sorted = imagesSource.sort(sortFramesByDate).filter(frame => {
    const uniqueKey = frame.lat + frame.lon;
    if (urlSet.has(uniqueKey)) {
      return false;
    }
    urlSet.add(uniqueKey);
    return true;
  });

  return sorted;
};

export const getImageArrayFirstIndex = (
  frames: IFrame[] | undefined,
): number => {
  if (frames === undefined || !frames?.length) return 0;
  const frameIdx = getMiddleElement(frames);
  return frameIdx;
};

export const getMiddleElement = (arr: any[]) => {
  return Math.floor((arr.length - 0.5) / 2);
};

export const findFrameWithLargestDetectionArea = (
  frames: IFrame[],
  detectionsByFrame: { [key: string]: IDetectionsByFrame[] },
) => {
  let maxArea = 0;
  let index: number = 0;

  Object.entries(detectionsByFrame).forEach(([frameIdx, detections]) => {
    detections.forEach(detection => {
      const [, , width, height] = detection?.detectionInfo || [];
      const area = (width ?? 0) * (height ?? 0);
      if (area > maxArea) {
        maxArea = area;
        index = frames.findIndex(frame => frame.idx === parseInt(frameIdx));
      }
    });
  });

  return index;
};

export const findIndexWithLargestDetectionArea = (detections: IDetection[]) => {
  const maxArea = 0;
  let index = -1;
  detections.forEach((detection, i) => {
    const [, , width, height] = detection || [];
    const area = (width ?? 0) * (height ?? 0);
    if (area > maxArea) {
      index = i;
    }
  });
  return index;
};

export const getNameSortedImages = (frames: IFrame[]): string[] => {
  const images: IFrame[] = frames?.map((frame: IFrame) => ({
    url: frame?.url,
    idx: Number(getFileNameFromS3Url(frame?.url)),
    lat: frame?.lat,
    lon: frame?.lon,
    t: frame?.t,
  }));

  // Sort images based on frame name
  images?.sort((img1: IFrame, img2: IFrame) => {
    if (img1.idx < img2.idx) return -1;
    if (img1.idx > img2.idx) return 1;
    return 0;
  });
  const sortedImageUrls = images?.map((image: IFrame) => image.url);
  return sortedImageUrls;
};

export const getFileNameFromS3Url = (url: string) => {
  return url?.split('?')[0].split('/').pop()?.split('.')[0] ?? '';
};

export const getDetections = (
  frames: IFrame[],
  detectionsByFrame: { [key: string]: IDetectionsByFrame[] },
): IDetection[] => {
  const detections: IDetection[] = [];
  const frameNames = frames?.map((frame: IFrame) =>
    getFileNameFromS3Url(frame?.url),
  );
  const sortedFrames = frameNames?.sort((a, b) => Number(a) - Number(b));
  sortedFrames?.forEach((frameName: string) => {
    const detectionInfo: IDetection = detectionsByFrame?.[frameName]?.[0]
      ?.detectionInfo as IDetection;
    if (detectionInfo) {
      detections.push(detectionInfo);
    } else {
      detections.push(null);
    }
  });
  return detections;
};

export const getCoordsFromFrames = (frames: IFrame[]): IPoints => {
  const images: IFrame[] = frames?.map((frame: IFrame) => ({
    url: frame?.url,
    idx: Number(getFileNameFromS3Url(frame?.url)),
    lat: frame?.lat,
    lon: frame?.lon,
    t: frame?.t,
  }));

  // Sort images based on frame name
  images?.sort((img1, img2) => {
    if (img1.idx < img2.idx) return -1;
    if (img1.idx > img2.idx) return 1;
    return 0;
  });
  const points: IPoints = images?.map((image: IFrame) => {
    return {
      lat: image.lat,
      lon: image.lon,
    };
  });
  return points;
};

const validOutcomes: IOutcome[] = [
  'approved',
  'rejected',
  'skip',
  'falsePositive',
  'badData',
  // TODO [TR-108]: Remove 'left' and 'right' after updating the backend to pass valid outcomes for each game
  'left',
  'right',
  'badSatellite',
  'badHypotheses',
];

export const isValidOutcome = (value: any): value is IOutcome =>
  validOutcomes.includes(value);

export const getNormalizedImages = (
  frames?: string[] | IFrame[],
  closeFrames?: ICloseFrame[],
  contentImages: string[] = [],
) => {
  if (frames) {
    if (typeof frames[0] === 'string') {
      // frames is a list of URLs
      return frames as string[];
    } else {
      const _frames = frames as IFrame[];
      return getImageArray(_frames, closeFrames).map(frame => frame.url);
    }
  }
  // Default to contentImages if no frames
  return contentImages;
};

export const getNormalizedPoints = (
  frames?: string[] | IFrame[],
  closeFrames?: ICloseFrame[],
  imagesCoords?: ICoordinates[],
) => {
  if (imagesCoords?.length) {
    // Use imageCoords if available
    const result = imagesCoords
      .map(coord => ({
        lat: coord.lat,
        lon: coord.lon,
      }))
      .filter(point => point.lat !== undefined && point.lon !== undefined);
    return result;
  }

  // Otherwise, use lat/lon from frames if available
  if (frames && typeof frames[0] !== 'string') {
    const _frames = frames as IFrame[];
    const result = getImageArray(_frames, closeFrames)
      .map(frame => ({
        lat: frame.lat,
        lon: frame.lon,
        yaw: frame.yaw,
      }))
      .filter(point => point.lat !== undefined && point.lon !== undefined);
    return result;
  }

  return [];
};

export const getNormalizedDetections = (
  frames?: string[] | IFrame[],
  closeFrames?: ICloseFrame[],
  detectionsByFrame?: { [key: string]: IDetectionsByFrame[] },
  detections?: IDetection[],
) => {
  if (detections?.length) {
    // If detections are directly provided in content.detections
    return detections;
  }

  if (frames && detectionsByFrame && typeof frames[0] !== 'string') {
    const _frames = frames as IFrame[];
    const frameIdToDetections = new Map<number, IDetection[]>();

    // Map frame id (idx) to detections from detectionsByFrame
    Object.entries(
      detectionsByFrame as { [key: string]: IDetectionsByFrame[] },
    ).forEach(([frameIdx, frameDetections]) => {
      const frameIndex = parseInt(frameIdx, 10);
      frameIdToDetections.set(
        frameIndex,
        frameDetections.map(detection => detection.detectionInfo),
      );
    });

    // Match detections to frames based on frame's idx and maintain the array size
    return getImageArray(_frames, closeFrames).map(frame => {
      const detections = frameIdToDetections.get(frame.idx);
      return detections && detections.length > 0 ? detections[0] : null;
    });
  }

  return [];
};
